import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { ProductCard } from "./HomePage";
import { LiveBadge } from "../components/Layout";
import { useAuth } from "../lib/AuthContext";
import { Users, Heart, Sparkles } from "lucide-react";
import StoreChatWidget from "../components/StoreChatWidget";

export default function StorePage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const invited = params.get("invite") === "1";
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [following, setFollowing] = useState(false);
  const [tab, setTab] = useState("products");

  const load = async () => {
    const { data } = await api.get(`/stores/${id}`);
    setData(data);
  };

  useEffect(() => { load(); }, [id]);

  // Keep follow state in sync once auth resolves (user may still be null at
  // mount when arriving via a shared /join link) so we never show the
  // "Follow" invite CTA to someone who already follows — tapping it would
  // toggle them OFF.
  useEffect(() => {
    setFollowing(!!user?.followed_stores?.includes(id));
  }, [user, id]);

  const toggleFollow = async () => {
    if (!user) return alert("Login to follow");
    const { data } = await api.post(`/stores/${id}/follow`);
    setFollowing(data.followed);
  };

  if (!data) return <div className="p-10 text-center text-[#A1A1AA]">Loading…</div>;
  const s = data.store;

  return (
    <div className="pb-24 md:pb-12">
      {invited && !following && (
        <div
          data-testid="invite-follow-banner"
          className="bg-[#D4FF00] text-black px-4 py-3 flex items-center justify-center gap-3 flex-wrap"
        >
          <span className="flex items-center gap-2 font-display font-black uppercase text-sm tracking-wide text-center">
            <Sparkles size={16} /> You're invited to {s.name}'s live sale
          </span>
          <button
            onClick={toggleFollow}
            data-testid="invite-follow-btn"
            className="bg-black text-[#D4FF00] font-bold text-xs uppercase tracking-widest px-4 py-2 rounded-full hover:scale-105 transition"
          >
            Follow for live alerts
          </button>
        </div>
      )}
      <div className="relative h-52 sm:h-72">
        <img src={s.banner_url} className="w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"/>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10">
        <div className="flex items-end gap-4">
          <img src={s.logo_url} className="w-24 h-24 rounded-2xl border-4 border-black bg-[#261534]" alt=""/>
          <div className="flex-1">
            <h1 className="font-display font-black text-3xl sm:text-5xl uppercase">{s.name}</h1>
            <div className="text-sm text-[#A1A1AA] flex items-center gap-3 mt-1">
              <span className="flex items-center gap-1"><Users size={12}/> {s.followers} followers</span>
              <span>★ {s.rating}</span>
            </div>
          </div>
          <button onClick={toggleFollow} data-testid="follow-store-btn" className={following?"btn-secondary":"btn-primary"}>
            <Heart size={14} className={"inline mr-1 "+(following?"fill-[#FF007F] text-[#FF007F]":"")} /> {following?"Following · alerts on":"Follow + live alerts"}
          </button>
        </div>
        <p className="text-[#A1A1AA] mt-3 max-w-2xl">{s.description}</p>

        <div className="flex gap-2 mt-8 border-b border-[#3D2845]">
          {[["products", `Products (${data.products.length})`],
            ["streams", `Live (${data.streams.length})`],
            ["campaigns", `Campaigns (${data.campaigns.length})`],
            ["videos", `Videos (${data.videos.length})`]].map(([k, l]) => (
            <button key={k} onClick={()=>setTab(k)} data-testid={`store-tab-${k}`}
              className={`px-4 py-3 text-sm font-bold uppercase tracking-wider border-b-2 transition ${tab===k?"border-[#D4FF00] text-[#D4FF00]":"border-transparent text-[#A1A1AA]"}`}>
              {l}
            </button>
          ))}
        </div>

        <div className="py-8">
          {tab === "products" && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {data.products.map((p, i) => <ProductCard key={p.product_id} p={p} index={i}/>)}
            </div>
          )}
          {tab === "streams" && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {data.streams.map(st => (
                <Link key={st.stream_id} to={`/live/${st.stream_id}`} className="card block relative">
                  <div className="aspect-video"><img src={st.thumbnail_url} className="w-full h-full object-cover"/></div>
                  {st.is_live && <div className="absolute top-3 left-3"><LiveBadge/></div>}
                  <div className="p-3 text-sm font-semibold">{st.title}</div>
                </Link>
              ))}
            </div>
          )}
          {tab === "campaigns" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.campaigns.map(c => (
                <Link key={c.campaign_id} to={`/campaign/${c.campaign_id}`} className="card flex gap-3">
                  <img src={c.banner_url} className="w-32 h-32 object-cover"/>
                  <div className="p-3 flex-1">
                    <div className="text-xs text-[#D4FF00] font-bold uppercase tracking-wider">-{c.offer_percent}%</div>
                    <div className="font-display text-lg font-bold">{c.title}</div>
                    <div className="text-xs text-[#A1A1AA] line-clamp-2 mt-1">{c.description}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
          {tab === "videos" && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {data.videos.map(v => (
                <div key={v.video_id} className="card">
                  <div className="aspect-video"><img src={v.thumbnail_url} className="w-full h-full object-cover"/></div>
                  <div className="p-3 text-sm font-semibold">{v.title}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <StoreChatWidget store={s}/>
    </div>
  );
}
